/**
 * MotionProjectsManager Component
 * Project-based manager for Motion Lab (Reels)
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus, Search, FolderOpen, Film, Clock, Trash2, MoreVertical,
  Grid, List, ArrowLeft, Edit3, Copy, Loader2,
  Instagram, Facebook, Youtube, Monitor, Video
} from 'lucide-react';
import ReelsEditor from './ReelsEditor';
import ReelsProjectService from '../../../services/ReelsProjectService';
import { ASPECT_RATIOS } from './utils/effectPresets';
import { formatDuration } from './utils/timeUtils';

const BASE_TRACKS = [
  { id: 'video-1', type: 'video', name: 'Video 1', clips: [], muted: false, locked: false, visible: true },
  { id: 'audio-1', type: 'audio', name: 'Audio 1', clips: [], muted: false, locked: false, volume: 1 }
];

const normalizeProject = (project) => ({
  ...project,
  settings: {
    width: 1080,
    height: 1920,
    frameRate: 30,
    duration: 0,
    ...project.settings
  },
  mediaLibrary: project.mediaLibrary || [],
  tracks: project.tracks && project.tracks.length ? project.tracks : BASE_TRACKS,
  textClips: project.textClips || [],
  shapeClips: project.shapeClips || [],
  stickerClips: project.stickerClips || []
});

const RATIO_ICONS = {
  '9:16': { icon: Instagram, color: '#E1306C', label: 'Reels/TikTok' },
  '1:1': { icon: Instagram, color: '#E1306C', label: 'Square' },
  '4:5': { icon: Instagram, color: '#E1306C', label: 'Portrait' },
  '16:9': { icon: Youtube, color: '#FF0000', label: 'YouTube' },
  '4:3': { icon: Facebook, color: '#1877F2', label: 'Standard' },
  '21:9': { icon: Monitor, color: '#0EA5E9', label: 'Cinematic' }
};

const getRatioIcon = (ratioId) => RATIO_ICONS[ratioId] || { icon: Video, color: '#64748B', label: 'Custom' };

const sanitizeProjectForSave = (project) => ({
  ...project,
  mediaLibrary: (project.mediaLibrary || []).map((item) => {
    const { file, ...rest } = item;
    return rest;
  })
});

const MotionProjectsManager = ({ onBack, featureFlags = {} }) => {
  const [projects, setProjects] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [sortBy, setSortBy] = useState('modifiedAt');
  const [showNewProjectModal, setShowNewProjectModal] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [projectMenuOpen, setProjectMenuOpen] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [menuProject, setMenuProject] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState(null);
  const todayLabel = new Date().toDateString();

  const fetchProjects = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await ReelsProjectService.listProjects({
        page: 1,
        limit: 50,
        search: searchQuery,
        sort: sortBy,
        order: sortBy === 'name' ? 'asc' : 'desc'
      });
      setProjects(response.projects || []);
    } catch (err) {
      console.error('Failed to fetch motion projects:', err);
      setError('Failed to load motion projects. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [searchQuery, sortBy]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const openProject = useCallback(async (projectId) => {
    setIsLoading(true);
    try {
      const fullProject = await ReelsProjectService.getProject(projectId);
      setEditingProject(normalizeProject(fullProject));
    } catch (err) {
      console.error('Failed to open motion project:', err);
      alert('Failed to open project. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const createProject = useCallback(async (ratio) => {
    setIsCreating(true);
    try {
      const created = await ReelsProjectService.createProject({
        name: 'Untitled Motion',
        settings: {
          width: ratio.width,
          height: ratio.height,
          frameRate: ratio.frameRate || 30,
          aspectRatio: ratio.id
        }
      });
      setShowNewProjectModal(false);
      if (created?.id) {
        await openProject(created.id);
      } else {
        fetchProjects();
      }
    } catch (err) {
      console.error('Failed to create motion project:', err);
      alert('Failed to create project. Please try again.');
    } finally {
      setIsCreating(false);
    }
  }, [fetchProjects, openProject]);

  const handleSaveProject = useCallback(async (projectData) => {
    try {
      const updated = await ReelsProjectService.updateProject(projectData.id, sanitizeProjectForSave(projectData));
      setEditingProject(normalizeProject(updated));
      setProjects((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
    } catch (err) {
      console.error('Failed to save motion project:', err);
      alert('Failed to save project. Please try again.');
    }
  }, []);

  const deleteProject = useCallback(async (projectId) => {
    if (!window.confirm('Are you sure you want to delete this project?')) {
      setProjectMenuOpen(null);
      return;
    }
    try {
      await ReelsProjectService.deleteProject(projectId);
      setProjects((prev) => prev.filter((p) => p.id !== projectId));
    } catch (err) {
      console.error('Failed to delete motion project:', err);
      alert('Failed to delete project. Please try again.');
    }
    setProjectMenuOpen(null);
  }, []);

  const duplicateProject = useCallback(async (project) => {
    try {
      const duplicated = await ReelsProjectService.duplicateProject(project.id, `${project.name} (Copy)`);
      setProjects((prev) => [duplicated, ...prev]);
    } catch (err) {
      console.error('Failed to duplicate motion project:', err);
      alert('Failed to duplicate project. Please try again.');
    }
    setProjectMenuOpen(null);
  }, []);

  const renameProject = useCallback(async (projectId, newName) => {
    try {
      const updated = await ReelsProjectService.updateProject(projectId, { name: newName });
      setProjects((prev) => prev.map((p) => (p.id === projectId ? updated : p)));
    } catch (err) {
      console.error('Failed to rename motion project:', err);
      alert('Failed to rename project. Please try again.');
    }
  }, []);

  const updatedToday = projects.filter((project) => new Date(project.modifiedAt || project.updatedAt).toDateString() === todayLabel).length;

  if (editingProject) {
    return (
      <ReelsEditor
        project={editingProject}
        onBack={() => {
          setEditingProject(null);
          fetchProjects();
        }}
        onSave={handleSaveProject}
        featureFlags={featureFlags}
      />
    );
  }

  return (
    <div className="flex flex-col h-full bg-[#f7f4ee] dark:bg-slate-950">
      {/* Main Content */}
      <div className="flex-1 overflow-auto">
        {/* Hero */}
        <div className="relative overflow-hidden px-6 py-10 sm:py-12">
          <div
            className="absolute inset-0"
            style={{
              background: 'linear-gradient(120deg, rgba(13, 148, 136, 0.98), rgba(15, 23, 42, 0.98))'
            }}
          />
          <div
            className="absolute inset-0 opacity-70"
            style={{
              backgroundImage: 'radial-gradient(circle at 15% 15%, rgba(255,255,255,0.35), transparent 40%), radial-gradient(circle at 80% 10%, rgba(255,255,255,0.25), transparent 35%)'
            }}
          />
          <div className="relative max-w-5xl mx-auto">
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
              <div className="text-white">
                <p className="text-sm uppercase tracking-[0.2em] text-white/70">Motion workspace</p>
                <h2 className="text-3xl sm:text-4xl font-semibold mt-2">All Motion Lab projects</h2>
                <p className="text-sm sm:text-base text-white/80 mt-2 max-w-xl">
                  Cut together reels, add audio, and export in the right format for every platform.
                </p>
              </div>
              <div className="w-full lg:w-[360px]">
                <label className="text-xs font-semibold text-white/80 uppercase tracking-[0.2em]">
                  Search projects
                </label>
                <div className="relative mt-3">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Search motion projects..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 bg-white/95 dark:bg-slate-800 border-0 rounded-xl shadow-lg text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-4 focus:ring-teal-200"
                  />
                </div>
                <div className="flex flex-wrap gap-2 mt-4 text-xs text-white/80">
                  <span className="px-3 py-1.5 rounded-full bg-white/15 text-xs font-medium">
                    {projects.length} projects
                  </span>
                  <span className="px-3 py-1.5 rounded-full bg-white/15 text-xs font-medium">
                    {updatedToday} updated today
                  </span>
                </div>
                <button
                  onClick={() => setShowNewProjectModal(true)}
                  className="mt-4 w-full px-4 py-2.5 bg-white text-slate-900 hover:bg-white/90 rounded-xl font-semibold transition-colors flex items-center justify-center gap-2"
                >
                  <Plus className="w-5 h-5" />
                  New Motion Project
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Start */}
        <div className="px-6 -mt-8">
          <div className="bg-white/95 dark:bg-slate-900/80 backdrop-blur rounded-2xl shadow-xl border border-white/60 dark:border-slate-800 p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Quick start</h3>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Choose a canvas ratio and start cutting footage.
                </p>
              </div>
              <button
                onClick={() => setShowNewProjectModal(true)}
                className="text-sm font-medium text-teal-700 hover:text-teal-600"
              >
                View all presets
              </button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              {ASPECT_RATIOS.slice(0, 5).map((ratio) => (
                (() => {
                  const RatioIcon = getRatioIcon(ratio.id).icon;
                  const ratioColor = getRatioIcon(ratio.id).color;
                  const ratioLabel = getRatioIcon(ratio.id).label;
                  return (
                    <button
                      key={ratio.id}
                      onClick={() => createProject(ratio)}
                      className="group p-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50/80 dark:bg-slate-800/80 hover:border-teal-400 hover:bg-white dark:hover:bg-slate-800 transition-all text-left"
                    >
                      <div
                        className="w-9 h-9 rounded-lg flex items-center justify-center mb-3"
                        style={{ backgroundColor: `${ratioColor}20` }}
                      >
                        <RatioIcon className="w-5 h-5" style={{ color: ratioColor }} />
                      </div>
                      <h4 className="text-sm font-semibold text-slate-900 dark:text-white">{ratioLabel}</h4>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                        {ratio.id} · {ratio.width} x {ratio.height}
                      </p>
                    </button>
                  );
                })()
              ))}
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="px-6 py-4 mt-6 border-b border-slate-200 dark:border-slate-800 bg-white/95 dark:bg-slate-900/80 backdrop-blur flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="relative">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-3 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
              >
                <option value="modifiedAt">Date modified</option>
                <option value="createdAt">Date created</option>
                <option value="name">Name</option>
              </select>
            </div>
            <div className="text-sm text-slate-500 dark:text-slate-400">
              {projects.length} results
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-lg transition-colors ${viewMode === 'grid' ? 'bg-teal-100 dark:bg-teal-900/50 text-teal-700' : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800'}`}
            >
              <Grid className="w-5 h-5" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-lg transition-colors ${viewMode === 'list' ? 'bg-teal-100 dark:bg-teal-900/50 text-teal-700' : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800'}`}
            >
              <List className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Projects Section */}
        <div className="p-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-20 text-slate-500">
              <Loader2 className="w-6 h-6 animate-spin mr-2" />
              Loading projects...
            </div>
          ) : error ? (
            <div className="text-center py-16 text-red-500">{error}</div>
          ) : projects.length === 0 ? (
            <div className="text-center py-16">
              <FolderOpen className="w-16 h-16 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-700 dark:text-slate-300 mb-2">
                Your motion library is empty
              </h3>
              <p className="text-slate-500 dark:text-slate-400 mb-6">
                Create your first reel to get started.
              </p>
              <button
                onClick={() => setShowNewProjectModal(true)}
                className="px-6 py-3 bg-teal-600 hover:bg-teal-500 text-white rounded-lg font-medium transition-colors inline-flex items-center gap-2"
              >
                <Plus className="w-5 h-5" />
                New Motion Project
              </button>
            </div>
          ) : viewMode === 'grid' ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {projects.map((project) => {
                const projectThumbnail = project.thumbnailUrl || project.thumbnail || project.thumbnail_url;
                return (
                  <div
                    key={project.id}
                    className="group relative bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 overflow-hidden hover:shadow-xl hover:-translate-y-0.5 transition-all cursor-pointer"
                    onClick={() => openProject(project.id)}
                  >
                    <div className="aspect-[9/16] bg-slate-100 dark:bg-slate-700 relative">
                      {projectThumbnail ? (
                        <img
                          src={projectThumbnail}
                          alt={project.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Film className="w-12 h-12 text-slate-300 dark:text-slate-600" />
                        </div>
                      )}
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/25 transition-colors flex items-center justify-center">
                        <span className="opacity-0 group-hover:opacity-100 px-3 py-1.5 bg-white dark:bg-slate-800 rounded-lg text-sm font-medium text-slate-700 dark:text-slate-200 shadow-lg transition-opacity">
                          Open
                        </span>
                      </div>
                    </div>
                    <div className="p-3">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <h4 className="text-sm font-medium text-slate-900 dark:text-white truncate">
                            {project.name}
                          </h4>
                          <div className="flex items-center gap-1.5 mt-1 text-xs text-slate-500 dark:text-slate-400">
                            <Clock className="w-3.5 h-3.5" />
                            {formatDuration(project.duration || 0)}
                          </div>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (projectMenuOpen === project.id) {
                              setProjectMenuOpen(null);
                              setMenuProject(null);
                            } else {
                              const rect = e.currentTarget.getBoundingClientRect();
                              const menuHeight = 130;
                              const spaceBelow = window.innerHeight - rect.bottom;
                              const top = spaceBelow < menuHeight ? rect.top - menuHeight - 4 : rect.bottom + 4;
                              setMenuPosition({
                                top,
                                left: Math.min(rect.right - 160, window.innerWidth - 168)
                              });
                              setProjectMenuOpen(project.id);
                              setMenuProject(project);
                            }
                          }}
                          className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <MoreVertical className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="space-y-2">
              {projects.map((project) => {
                const projectThumbnail = project.thumbnailUrl || project.thumbnail || project.thumbnail_url;
                return (
                  <div
                    key={project.id}
                    className="group flex items-center gap-4 p-3 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 hover:shadow-md transition-all cursor-pointer"
                    onClick={() => openProject(project.id)}
                  >
                    <div className="w-16 h-20 bg-slate-100 dark:bg-slate-700 rounded overflow-hidden flex-shrink-0">
                      {projectThumbnail ? (
                        <img
                          src={projectThumbnail}
                          alt={project.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Film className="w-6 h-6 text-slate-300 dark:text-slate-600" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-medium text-slate-900 dark:text-white truncate">
                        {project.name}
                      </h4>
                      <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                        <span>{project.settings?.width} x {project.settings?.height}</span>
                        <span>{formatDuration(project.duration || 0)}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 text-xs text-slate-500">
                      <Clock className="w-3.5 h-3.5" />
                      {new Date(project.modifiedAt || project.updatedAt).toLocaleDateString()}
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (projectMenuOpen === project.id) {
                          setProjectMenuOpen(null);
                          setMenuProject(null);
                        } else {
                          const rect = e.currentTarget.getBoundingClientRect();
                          const menuHeight = 130;
                          const spaceBelow = window.innerHeight - rect.bottom;
                          const top = spaceBelow < menuHeight ? rect.top - menuHeight - 4 : rect.bottom + 4;
                          setMenuPosition({
                            top,
                            left: Math.min(rect.right - 160, window.innerWidth - 168)
                          });
                          setProjectMenuOpen(project.id);
                          setMenuProject(project);
                        }
                      }}
                      className="p-2 rounded hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-400"
                    >
                      <MoreVertical className="w-4 h-4" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* New Project Modal */}
      {showNewProjectModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
            <div className="p-6 border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/70">
              <h2 className="text-xl font-semibold text-slate-900 dark:text-white">
                Start a new motion project
              </h2>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                Choose a canvas ratio to get started.
              </p>
            </div>
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {ASPECT_RATIOS.map((ratio) => (
                  (() => {
                    const RatioIcon = getRatioIcon(ratio.id).icon;
                    const ratioColor = getRatioIcon(ratio.id).color;
                    return (
                      <button
                        key={ratio.id}
                        onClick={() => createProject(ratio)}
                        className="group p-4 bg-slate-50 dark:bg-slate-800 hover:bg-white dark:hover:bg-slate-700 rounded-xl border-2 border-transparent hover:border-teal-400 transition-all text-left"
                        disabled={isCreating}
                      >
                        <div
                          className="w-12 h-12 rounded-lg flex items-center justify-center mb-3"
                          style={{ backgroundColor: `${ratioColor}20` }}
                        >
                          <RatioIcon className="w-6 h-6" style={{ color: ratioColor }} />
                        </div>
                        <h3 className="font-medium text-slate-900 dark:text-white text-sm">
                          {ratio.name}
                        </h3>
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                          {ratio.width} x {ratio.height} px
                        </p>
                      </button>
                    );
                  })()
                ))}
              </div>
            </div>
            <div className="p-4 border-t border-slate-200 dark:border-slate-800 flex justify-end">
              <button
                onClick={() => setShowNewProjectModal(false)}
                className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Dropdown Menu - rendered at root level to escape overflow:hidden */}
      {projectMenuOpen && menuProject && (
        <>
          {/* Click outside to close menu */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => {
              setProjectMenuOpen(null);
              setMenuProject(null);
            }}
          />
          {/* Menu */}
          <div
            className="fixed z-50 w-40 bg-white dark:bg-slate-800 rounded-lg shadow-xl border border-slate-200 dark:border-slate-700 py-1"
            style={{
              top: menuPosition.top,
              left: menuPosition.left
            }}
          >
            <button
              onClick={() => {
                const newName = window.prompt('Enter new name:', menuProject.name);
                if (newName && newName.trim()) {
                  renameProject(menuProject.id, newName.trim());
                }
                setProjectMenuOpen(null);
                setMenuProject(null);
              }}
              className="w-full px-3 py-2 text-left text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-2"
            >
              <Edit3 className="w-4 h-4" />
              Rename
            </button>
            <button
              onClick={() => {
                duplicateProject(menuProject);
                setMenuProject(null);
              }}
              className="w-full px-3 py-2 text-left text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-2"
            >
              <Copy className="w-4 h-4" />
              Duplicate
            </button>
            <button
              onClick={() => {
                deleteProject(menuProject.id);
                setMenuProject(null);
              }}
              className="w-full px-3 py-2 text-left text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2"
            >
              <Trash2 className="w-4 h-4" />
              Delete
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default MotionProjectsManager;
